/**
 * USER AWARDS API
 *
 * These hooks call /awards endpoints for user-facing award/incentive interactions.
 * Use for non-admin users who need to view/claim awards and incentives.
 *
 * IMPORTANT: This is different from src/queries/incentives.ts which calls
 * admin endpoints (/admin/incentives) and requires admin permissions.
 *
 * Alias: @bonusly/user-awards (renamed from @bonusly/user-incentives for semantic correctness)
 *
 * Note: The /awards endpoint handles both awards and incentives based on the 'type' parameter:
 * - type=claimable → returns incentives (ClaimableAward model)
 * - no type → returns all awards
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { Permission, useIdentity, usePermission } from '@bonusly/who-am-i';

import { ApprovalRequestParams, IncentiveDTO, IncentivesDTO } from '../lib/entities';
import { APIResponse, ApprovalRequestResponse, IncentiveDetailsResponse, IncentivesResponse } from '../lib/types';

enum QueryKey {
	Incentives = 'incentives',
	Incentive = 'incentive-data',
	UserProfile = 'recognition-user',
}

export const useAwards = () => {
	const http = useHttpClient();
	const canAccessIncentivesExperience = usePermission(Permission.CanAccessIncentivesExperience);

	return useQuery(
		[QueryKey.Incentives],
		() =>
			http.get<APIResponse<IncentivesResponse>>('/awards', {
				params: { view: 'normal', type: 'claimable' },
			}),
		{
			enabled: canAccessIncentivesExperience,
			retry: false,
			refetchOnWindowFocus: false,
			select: (data) => {
				if (!data || !data.data || !data.data.success) {
					throw new Error('Failed to fetch incentives');
				}
				return new IncentivesDTO(data.data.result);
			},
		}
	);
};

export const useAward = (id?: string) => {
	const http = useHttpClient();
	const canAccessIncentivesExperience = usePermission(Permission.CanAccessIncentivesExperience);

	return useQuery(
		[QueryKey.Incentive, id],
		() =>
			http.get<APIResponse<IncentiveDetailsResponse>>(`/awards/${id}`, {
				params: { view: 'claim_modal' },
			}),
		{
			enabled: !!id && canAccessIncentivesExperience,
			retry: false,
			refetchOnWindowFocus: false,
			select: (data) => {
				if (!data || !data.data || !data.data.success || !data.data.result) {
					throw new Error(`Failed to fetch incentive with id ${id}`);
				}
				return new IncentiveDTO(data.data.result);
			},
		}
	);
};

export const useCreateApprovalRequest = (options = {}) => {
	const http = useHttpClient();
	return useMutation(
		(params: ApprovalRequestParams) => {
			const data = new FormData();
			data.append('award_id', params.id);
			data.append('giver_id', params.giverId || '');
			if (params.attachment) {
				data.append('claim_attachment', params.attachment);
			}
			if (params.reason) {
				data.append('claim_reason', params.reason);
			}

			return http.post<ApprovalRequestResponse>('/approval_requests', data);
		},
		{
			retry: false,
			onSuccess: (data: any) => {
				if (!data || !data.data || !data.data.success) {
					throw new Error('Failed to create approval request');
				}
				return data;
			},
			...options,
		}
	);
};

export const useInvalidateQueries = () => {
	const queryClient = useQueryClient();
	const { refetchProfile } = useIdentity();
	return {
		invalidate: (id?: string) => {
			queryClient.invalidateQueries([QueryKey.Incentives]);
			queryClient.invalidateQueries([QueryKey.Incentive, id]);
			// NOTE: invalidating query and refetching profile to keep both user data calls up to date
			// bonus-feed MFE uses 'recognition-user'
			queryClient.invalidateQueries([QueryKey.UserProfile]);
			refetchProfile();
		},
	};
};
